
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { SharedModule } from '../../@commons/ui-components/shared.module';
import { ArticleComponent } from "../../@commons/@components/article/article.component";
import { TaskApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/task/task-api';
import {
  TaskListRequest
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/location/task-list.request';
import { StorageService } from '../../@commons/@services/front/storage.service';
import {
  TaskListResponse
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/task-list.response';
import { ArticleListRequest } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/article/article-list.request';
import { ArticleApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/article/article-api';
import { Router } from '@angular/router';
import RoutesConstants from '../../@commons/constants/routes.constants';
import { TaskState } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/task-state.enum';
import { LabelPipe } from '../../@commons/pipes/LabelPipe';
import { VwDatePipe } from '../../@commons/pipes/vw-date.pipe';
import { ChoiceOption } from '../../@commons/ui-components/choice-selector/choice-selector.component';
import { CheckIconComponent } from '../../@icons/check-icon.component';
import { CloseIconComponent } from '../../@icons/close-icon.component';
import { AgendaEmptyComponent } from '../agenda-empty/agenda-empty.component';
import { AGENDA_ARTICLES_COUNT, ARCHIVED_TASK_STATES } from '../agenda.constants';

@Component({
    selector: 'vw-agenda-archives', // Indicates this is a standalone component
    imports: [SharedModule, TranslatePipe, ArticleComponent, AgendaEmptyComponent, LabelPipe, VwDatePipe, CheckIconComponent, CloseIconComponent], // Modules required by this component
    templateUrl: './agenda-archives.component.html', // HTML template for the component
    styleUrl: './agenda-archives.component.scss', // Stylesheet for the component
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [{
            provide: TaskApi,
            useClass: TaskApi
        }, {
            provide: ArticleApi,
            useClass: ArticleApi
        }]
})
export class AgendaArchivesComponent implements OnInit {

  /**
   * List of archived tasks.
   * @type {TaskListResponse[]}
   */
  tasks: TaskListResponse[] = [];
  // Pages numérotées à partir de 1 : le serveur ramène toute page < 1 à la page 1.
  currentTaskPage: number = 1;
  taskPageSize: number = 20;
  /** Tâches de l'état choisi (ou de tous) côté serveur : « Voir plus » tant qu'on en a moins. */
  totalTasks = 0;
  /** État choisi par le filtre ; rien = toutes les archives. */
  filter?: TaskState;
  /** Les trois filtres ; le compte arrive après, sans lui ils restent utilisables. */
  filterOptions: ChoiceOption[] = this.optionsWithCounts();
  articles: any[] = [];
  isLoading: boolean = false;
  isLoadingMore = false;
  /** Les tâches ont répondu : l'état vide ne s'affiche pas sur une panne. */
  tasksLoaded = false;
  /** Chaque chargement de la première page invalide les réponses encore en vol. */
  private loadVersion = 0;
  /**
   * List of advices related to the agenda.
   * @type {any[]}
   */
  advices: any[] = [];

  /**
   * Initializes the AgendaArchivesComponent with required services.
   * @param {TaskApi} taskApi - Service to fetch agenda-related data
   * @param storageService
   */
  constructor(private taskApi: TaskApi,
    private storageService: StorageService,
    private articleApi: ArticleApi,
    private router: Router) { }

  /**
   * Lifecycle hook that is called after data-bound properties are initialized.
   * Subscribes to tasks and advices data from AgendaService.
   */
  async ngOnInit() {
    this.isLoading = true;
    try {
      await Promise.all([this.loadTasks(), this.loadCounts()]);
      await this.loadArticles();
    } catch (e) {
      console.error('AgendaArchivesComponent: failed to initialize', e);
    } finally {
      this.isLoading = false;
    }
  }

  /** Une page de tâches archivées : les trois états, ou celui du filtre. */
  private listTasks(states: TaskState[], page: number, limit: number) {
    // Typé avec la vraie requête : celle de `TaskApi.list` (SDK 1.38.0) n'a pas `taskStates`.
    const taskFilters: TaskListRequest = { taskStates: states };
    return this.taskApi.list(taskFilters, page, limit, this.storageService.getCurrentLanguage());
  }

  private get selectedStates(): TaskState[] {
    return this.filter ? [this.filter] : ARCHIVED_TASK_STATES;
  }

  /** Première page de l'état choisi ; une réponse d'un filtre dépassé est laissée de côté. */
  async loadTasks() {
    const version = ++this.loadVersion;
    this.tasks = [];
    this.totalTasks = 0;
    this.tasksLoaded = false;
    const response = await this.listTasks(this.selectedStates, 1, this.taskPageSize);
    if (version !== this.loadVersion) return;
    this.currentTaskPage = 1;
    this.tasks = response.results;
    this.totalTasks = response.page.totalRecords;
    this.tasksLoaded = true;
  }

  /** Page suivante, à la suite ; sur une panne, la liste reste et « Voir plus » réessaie. */
  async loadMoreTasks() {
    if (this.isLoadingMore) return;
    const version = this.loadVersion;
    this.isLoadingMore = true;
    try {
      const response = await this.listTasks(this.selectedStates, this.currentTaskPage + 1, this.taskPageSize);
      if (version !== this.loadVersion) return;
      this.currentTaskPage++;
      this.tasks = [...this.tasks, ...response.results];
    } catch (e) {
      console.error('AgendaArchivesComponent: failed to load more tasks', e);
    } finally {
      this.isLoadingMore = false;
    }
  }

  /** Un toucher choisit l'état ; un second, qui renvoie `undefined`, le retire. */
  async selectFilter(state?: TaskState) {
    this.filter = state;
    try {
      await this.loadTasks();
    } catch (e) {
      console.error('AgendaArchivesComponent: failed to filter tasks', e);
    }
  }

  /** Le nombre de tâches de chaque état, un appel d'une tâche chacun (`page.totalRecords`). */
  async loadCounts() {
    try {
      const pages = await Promise.all(ARCHIVED_TASK_STATES.map((state) => this.listTasks([state], 1, 1)));
      this.filterOptions = this.optionsWithCounts(pages.map((page) => page.page.totalRecords));
    } catch (e) {
      console.error('AgendaArchivesComponent: failed to count tasks', e);
    }
  }

  private optionsWithCounts(counts: number[] = []): ChoiceOption[] {
    return ARCHIVED_TASK_STATES.map((state, i) => ({ value: state, label: `agenda.filters.${state}`, count: counts[i] }));
  }

  async loadArticles() {
    const articles = await this.articleApi.list(new ArticleListRequest(), 1, AGENDA_ARTICLES_COUNT, this.storageService.getCurrentLanguage());
    this.articles = articles.results;
  }

  isDone(task: TaskListResponse): boolean {
    return task.taskState === TaskState.DONE;
  }

  /** « Faite », « Passée » ou « Annulée » ; un état inattendu se lit « Passée ». */
  stateKey(task: TaskListResponse): 'DONE' | 'SKIP' | 'CANCELED' {
    if (task.taskState === TaskState.DONE || task.taskState === TaskState.CANCELED) return task.taskState as 'DONE' | 'CANCELED';
    return 'SKIP';
  }

  onAllArticles() {
    this.router.navigate([RoutesConstants.ARTICLES]);
  }
}
